import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { StatusChip } from './StatusChip'
import type { FormulationStatus, StatusHistoryEntry } from '../api/types'

/**
 * Collapsible panel listing the formulation's state-machine history
 * (Sprint 08b — Item 4). Newest entries first (the backend already
 * sorts that way via the relationship's order_by).
 *
 * Each row shows: from→to chips · transitioned_by · ISO date · comment.
 * Empty history = nothing rendered (caller decides whether to render
 * the panel at all).
 */

export interface HistoryPanelProps {
  history: StatusHistoryEntry[]
  defaultOpen?: boolean
}

export function HistoryPanel({ history, defaultOpen = false }: HistoryPanelProps) {
  const [open, setOpen] = useState(defaultOpen)

  if (history.length === 0) return null

  return (
    <section className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex w-fit cursor-pointer items-center gap-1 rounded-xs border-none bg-transparent p-0',
          'text-role-section-label text-secondary hover:text-primary',
        )}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Status history ({history.length})
      </button>
      {open ? (
        <ol className="flex flex-col gap-1 text-sm">
          {history.map((entry) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-center gap-2 rounded-sm border border-border-subtle bg-surface px-2 py-1.5"
            >
              {entry.from_status ? (
                <StatusChip status={entry.from_status as FormulationStatus} />
              ) : (
                <span className="text-tertiary">—</span>
              )}
              <span className="text-tertiary">→</span>
              <StatusChip status={entry.to_status as FormulationStatus} />
              <span className="text-role-metadata">
                {entry.transitioned_by ?? 'unknown'}
              </span>
              <span className="text-role-metadata text-tertiary">
                ·{' '}
                {entry.transitioned_at
                  ? new Date(entry.transitioned_at).toLocaleString()
                  : 'unknown date'}
              </span>
              {entry.comment ? (
                <span className="w-full text-role-data-secondary italic">
                  “{entry.comment}”
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  )
}
